<template>
  <div :class="$style.infoBox" data-info-box>
    <button
      :class="$style.infoButton"
      @click="toggleInfo"
      @blur="handleBlur"
      type="button"
      :aria-label="`Info about ${title}`"
    >
      <InfoIcon :size="16" />
    </button>

    <div v-if="isOpen" :class="$style.infoPopup" @click.stop>
      <div :class="$style.infoHeader">
        <h4 :class="$style.infoTitle">{{ title }}</h4>
        <button
          :class="$style.closeButton"
          @click="closeInfo"
          type="button"
          aria-label="Close info"
        >
          <CloseIcon :size="14" />
        </button>
      </div>
      <div :class="$style.infoContent">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { InfoIcon, CloseIcon } from "./icons";

defineProps<{
  title: string;
}>();

const isOpen = ref(false);

function toggleInfo() {
  isOpen.value = !isOpen.value;
}

function closeInfo() {
  isOpen.value = false;
}

function handleBlur(event: FocusEvent) {
  // Close info box when clicking outside
  setTimeout(() => {
    if (
      !event.relatedTarget ||
      !(event.relatedTarget as Element).closest("[data-info-box]")
    ) {
      isOpen.value = false;
    }
  }, 100);
}
</script>

<style module>
.infoBox {
  position: relative;
  display: inline-block;
}

.infoButton {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.infoButton:hover {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.1);
}

.infoPopup {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 16px;
  min-width: 280px;
  max-width: 350px;
  z-index: 1000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
}

.infoPopup::before {
  content: "";
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid rgba(255, 255, 255, 0.95);
}

.infoHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.infoTitle {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

.closeButton {
  background: none;
  border: none;
  color: rgba(0, 0, 0, 0.6);
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.closeButton:hover {
  color: rgba(0, 0, 0, 0.9);
  background: rgba(0, 0, 0, 0.1);
}

.infoContent {
  color: rgba(0, 0, 0, 0.8);
  font-size: 0.9rem;
  line-height: 1.5;
}

.infoContent p {
  margin: 0 0 8px 0;
}

.infoContent p:last-child {
  margin-bottom: 0;
}

.infoContent strong {
  color: #333;
  font-weight: 600;
}

.infoContent code {
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 4px;
  border-radius: 3px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 0.85rem;
  color: #333;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .infoPopup {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin-top: 0;
    min-width: 90vw;
    max-width: 90vw;
  }

  .infoPopup::before {
    display: none;
  }
}
</style>
